import { Button } from '@/shared/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { useCharacters } from '@/features/characters/queries/character.queries'
import {
    CameraAngles,
    CameraMovements,
    FieldHelpers,
    Framings,
    Shot,
    ShotPriorities,
    ShotTypes,
} from '@storyverse/shared'
import { useEffect, useState } from 'react'
import ShotCharacterVariantPicker from './ShotCharacterVariantPicker'
import { SHOTLIST_MAX_CHARACTER_VARIANTS } from '../config/shotlist.config'

type AddShotDialogProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    onAddShot: (shot: Omit<Shot, 'id'>) => void
}

const initialShot: Omit<Shot, 'id'> = {
    description: '',
    shotType: 'Other',
    cameraMovement: 'Static',
    cameraAngle: 'Eye Level',
    framing: 'Other',
    subject: '',
    emotionalPurpose: '',
    duration: '',
    shotPriority: 'Good to Have',
}

export default function AddShotDialog({ open, onOpenChange, onAddShot }: AddShotDialogProps) {
    const [shot, setShot] = useState<Omit<Shot, 'id'>>(initialShot)
    const { project } = useWorkspace()
    const { data: characters = [] } = useCharacters(project?.id ?? '')

    useEffect(() => {
        if (open) {
            setShot(initialShot)
        }
    }, [open])

    const handleChange = <K extends keyof Omit<Shot, 'id'>>(
        field: K,
        value: Omit<Shot, 'id'>[K]
    ) => {
        setShot((prev) => ({ ...prev, [field]: value }))
    }

    const handleSubmit = () => {
        console.log('Adding new shot with data:', shot)
        onAddShot(shot)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Add Shot</DialogTitle>
                    <DialogDescription>
                        Create a shot manually. New shots keep their creation order by default, and
                        you can rearrange them later.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-2">
                    <div className="grid gap-2">
                        <label className="text-sm font-medium">Subject</label>
                        <Input
                            value={shot.subject || ''}
                            onChange={(e) => handleChange('subject', e.target.value)}
                            placeholder={FieldHelpers.subject}
                        />
                    </div>

                    <div className="grid gap-2">
                        <label className="text-sm font-medium">Description</label>
                        <Textarea
                            value={shot.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder={FieldHelpers.description}
                            className="min-h-[100px]"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Shot Type</label>
                            <Select
                                value={shot.shotType}
                                onValueChange={(val) =>
                                    handleChange('shotType', val as Shot['shotType'])
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Shot Type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ShotTypes.map((item) => (
                                        <SelectItem key={item} value={item}>
                                            {item}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Priority</label>
                            <Select
                                value={shot.shotPriority || 'Good to Have'}
                                onValueChange={(val) =>
                                    handleChange('shotPriority', val as Shot['shotPriority'])
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Priority" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ShotPriorities.map((item) => (
                                        <SelectItem key={item} value={item}>
                                            {item}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Angle</label>
                            <Select
                                value={shot.cameraAngle || 'Eye Level'}
                                onValueChange={(val) =>
                                    handleChange('cameraAngle', val as Shot['cameraAngle'])
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Angle" />
                                </SelectTrigger>
                                <SelectContent>
                                    {CameraAngles.map((item) => (
                                        <SelectItem key={item} value={item}>
                                            {item}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Movement</label>
                            <Select
                                value={shot.cameraMovement}
                                onValueChange={(val) =>
                                    handleChange('cameraMovement', val as Shot['cameraMovement'])
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Movement" />
                                </SelectTrigger>
                                <SelectContent>
                                    {CameraMovements.map((item) => (
                                        <SelectItem key={item} value={item}>
                                            {item}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Framing</label>
                            <Select
                                value={shot.framing || 'Other'}
                                onValueChange={(val) =>
                                    handleChange('framing', val as Shot['framing'])
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Framing" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Framings.map((item) => (
                                        <SelectItem key={item} value={item}>
                                            {item}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Emotional Purpose</label>
                            <Input
                                value={shot.emotionalPurpose || ''}
                                onChange={(e) => handleChange('emotionalPurpose', e.target.value)}
                                placeholder={FieldHelpers.emotionalPurpose}
                            />
                        </div>
                        <div className="grid gap-2">
                            <label className="text-sm font-medium">Duration</label>
                            <Input
                                value={shot.duration || ''}
                                onChange={(e) => handleChange('duration', e.target.value)}
                                placeholder={FieldHelpers.duration}
                            />
                        </div>
                    </div>

                    <ShotCharacterVariantPicker
                        characters={characters}
                        selected={shot.characterVariants || []}
                        onChange={(characterVariants) =>
                            handleChange('characterVariants', characterVariants)
                        }
                        maxSelections={SHOTLIST_MAX_CHARACTER_VARIANTS}
                    />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit}>Add Shot</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
